import { useParams } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDay } from '../../lib/time';
import { useMeContext } from '../../app/MeContext';
import { Notice, Pill } from '../../ui/parts';
import { useEnterTenant, useNewInvite, useSuspendTenant, useTenant, useTenants } from '../api';
import { ConnectBotSheet, InviteSheet, PlanSheet } from '../InviteSheets';

/** Карточка организации: приглашение, состояние, бот, тариф, приостановка. */
export function TenantCard() {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const { id = '' } = useParams();
  const tenants = useTenants();
  const overview = useTenant(id);
  const newInvite = useNewInvite();
  const suspend = useSuspendTenant();
  const enter = useEnterTenant();

  useScreen('Организация', null);

  const t = overview.data;
  const isPlatform = tenants.data?.platformTenant === id;

  if (overview.isPending || !t) return <Screen><p className="lead">Загружаем организацию…</p></Screen>;

  const inviteLink = t.pendingInvite && t.bot.username
    ? `https://t.me/${t.bot.username}?startapp=join_${t.pendingInvite.code}`
    : t.pendingInvite && tenants.data?.platformBot
      ? `https://t.me/${tenants.data.platformBot}?startapp=join_${t.pendingInvite.code}`
      : null;

  return (
    <Screen>
      <div>
        <p className="eyebrow">{t.title}</p>
        <p className="lead" style={{ marginTop: 6 }}>
          {t.id} · создана {formatDay(t.createdAt, me.center.tz)}
        </p>
      </div>

      {t.pendingInvite ? (
        <>
          <p className="eyebrow">Ссылка для администратора</p>
          <div className="field" style={{ marginTop: 10 }}>
            <input type="text" readOnly value={inviteLink ?? 'ссылка появится вместе с ботом'} onFocus={(e) => e.currentTarget.select()} />
            <span className="hint">
              Одноразовая, 14 дней. По ней человек попадёт сразу в админку своей организации
              {t.bot.connected ? '' : ' — вход через ваш бот, пока у неё нет своего'}.
            </span>
          </div>
          <div className="row-actions">
            <button
              className="btn"
              disabled={!inviteLink}
              onClick={() => {
                if (!inviteLink) return;
                void navigator.clipboard?.writeText(inviteLink);
                toast('Ссылка скопирована');
              }}
            >
              Скопировать
            </button>
            <button
              className="btn solid"
              onClick={() => sheet.open(<InviteSheet tenantId={t.id} title={t.title} link={inviteLink} code={t.pendingInvite!.code} />)}
            >
              Отправить в Telegram
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="eyebrow">Администратор</p>
          <div className="rows" style={{ marginTop: 10 }}>
            {t.admins.length ? (
              t.admins.map((a) => (
                <div className="rw" key={a.id}>
                  <span className="rw-body">
                    <b>{a.name}</b>
                    <span>вошёл по приглашению</span>
                  </span>
                  <Pill kind="paid">на месте</Pill>
                </div>
              ))
            ) : (
              <p className="lead">Администратор пока не назначен. Создайте ссылку для подключения.</p>
            )}
          </div>
          <button
            className="btn"
            style={{ marginTop: 10 }}
            disabled={newInvite.isPending}
            onClick={() =>
              newInvite.mutate(
                { id: t.id },
                {
                  onSuccess: (res) => sheet.open(<InviteSheet tenantId={t.id} title={t.title} link={res.link} code={res.invite.code} />),
                  onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось выпустить ссылку'),
                },
              )
            }
          >
            Создать ссылку для администратора
          </button>
          <p className="footnote" style={{ textAlign: 'left' }}>
            При создании новой ссылки предыдущая станет недействительной.
          </p>
        </>
      )}

      {isPlatform ? null : (
        <>
          <p className="eyebrow" style={{ marginTop: 4 }}>Доступ</p>
          <button
            className="btn"
            disabled={enter.isPending || t.status === 'archived'}
            onClick={() =>
              enter.mutate(t.id, {
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось войти'),
              })
            }
          >
            {enter.isPending ? 'Входим…' : 'Войти как администратор'}
          </button>
          <p className="footnote" style={{ textAlign: 'left' }}>
            Откроется админка этой организации с вашими правами владельца. Вход и все действия
            попадут в её журнал действий — администратор организации их увидит.
          </p>
        </>
      )}

      <p className="eyebrow" style={{ marginTop: 4 }}>Состояние</p>
      <div className="kv">
        <div>
          <span className="k">Статус</span>
          <span className="v">{t.status === 'active' ? 'Активна' : t.status === 'suspended' ? 'Приостановлена' : 'Закрыта'}</span>
        </div>
        <div>
          <span className="k">Тариф</span>
          <span className="v">
            {planLabel(t.subscription?.status)}
            <button type="button" onClick={() => sheet.open(<PlanSheet tenantId={t.id} title={t.title} status={t.subscription?.status ?? 'trial'} />)}>
              Изменить
            </button>
          </span>
        </div>
        <div>
          <span className="k">Администраторов</span>
          <span className="v">{t.counts.admins}</span>
        </div>
        <div>
          <span className="k">Специалистов</span>
          <span className="v">{t.counts.specialists}</span>
        </div>
        <div>
          <span className="k">Клиентов</span>
          <span className="v">{t.counts.children}</span>
        </div>
        <div>
          <span className="k">Записей</span>
          <span className="v">{t.counts.appointments}</span>
        </div>
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>{isPlatform ? 'Бот платформы' : 'Бот организации'}</p>
      {t.bot.connected ? (
        <>
          <div className="rw">
            <span className="rw-body">
              <b>@{t.bot.username}</b>
              <span>Webhook настроен автоматически.</span>
            </span>
            <Pill kind="paid">Подключён</Pill>
          </div>
          {isPlatform ? (
            <p className="footnote" style={{ textAlign: 'left' }}>
              Через этот бот владелец управляет платформой, а администраторы новых организаций
              выполняют первичное подключение.
            </p>
          ) : (
            <button className="btn" style={{ marginTop: 10 }} onClick={() => sheet.open(<ConnectBotSheet tenantId={t.id} replace />)}>
              Заменить бота
            </button>
          )}
        </>
      ) : (
        <>
          <Notice>
            Своего бота нет: клиенты этой организации пока живут в вашем. Подключите её бота —
            родители увидят её имя и аватар.
          </Notice>
          <button className="btn" style={{ marginTop: 10 }} onClick={() => sheet.open(<ConnectBotSheet tenantId={t.id} />)}>
            Подключить бота организации
          </button>
        </>
      )}

      {isPlatform ? (
        <Notice kind="calm">
          Организацию платформы нельзя приостановить — она используется для управления платформой и
          подключения новых организаций.
        </Notice>
      ) : (
        <div className="row-actions" style={{ marginTop: 4 }}>
          <button
            className="btn"
            onClick={() => sheet.open(<PlanSheet tenantId={t.id} title={t.title} status={t.subscription?.status ?? 'trial'} />)}
          >
            Тариф
          </button>
          <button
            className={t.status === 'active' ? 'btn ghost-danger' : 'btn solid'}
            disabled={suspend.isPending}
            onClick={() =>
              suspend.mutate(
                { id: t.id, suspended: t.status === 'active' },
                {
                  onSuccess: () => toast(t.status === 'active' ? 'Организация приостановлена' : 'Организация включена'),
                  onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
                },
              )
            }
          >
            {t.status === 'active' ? 'Приостановить' : 'Возобновить работу'}
          </button>
        </div>
      )}

      {t.status !== 'active' ? (
        <Notice>
          Организация приостановлена. Доступ для сотрудников и клиентов временно закрыт до
          возобновления работы.
        </Notice>
      ) : null}
    </Screen>
  );
}

function planLabel(status: string | undefined): string {
  switch (status) {
    case 'active': return 'Активна';
    case 'past_due': return 'Просрочена';
    case 'readonly': return 'Только чтение';
    case 'cancelled': return 'Расторгнута';
    case 'trial': return 'Пробный период';
    default: return 'Не задан';
  }
}
